import { Grid } from '../../../../../Common/Grid'
import { Button, Input } from '@fullscreendigital/carrefour-dex-fe'
import { generatePath, useLocation, useNavigate } from "react-router-dom"
import { useNotification } from '../../../../../../../hooks'
import { useMemo, useState } from 'react'
import { useFormik } from 'formik'
import { formatErrorMessage } from '../../../../../../../utils/formatting'
import { useQuery } from 'react-query'
import { makeCoreGetRequest, makeCorePostRequest } from '../../../../../../../utils/api'
import ImageUpload from '../../../../Common/ImageUpload/ImageUpload'
import { getValidationSchema, initialValues } from './helpers'
import { CircularProgress, Typography } from "@mui/material"
import { FormInfoItems } from 'components/core-ui/FormInfoItems'
import { URLS } from "../../../../constants"

const RegisterTruckStep3 = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { showNotification } = useNotification()
  const [sealNumbers, setSealNumbers] = useState([''])
  const [numFields, setNumFields] = useState(1)
  const [uploadedImages, setUploadedImages] = useState([false])
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { truckId } = location.state

  const validationSchema = useMemo(() => getValidationSchema(), [])
  const formik = useFormik({
    enableReinitialize: false,
    validateOnChange: false,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async () => {}
  })

  const { data: hubName, isLoading: isLoadingHubName } = useQuery(
    ['hub-name'],
    async () => {
      const response = await makeCoreGetRequest(`/shop-reception-mobile/home/hub-name`, null)
      return response || []
    },
    {
      onError: error => {
        showNotification({
          message: `A aparut o eroare la obtinerea datelor despre hub. ${formatErrorMessage(
            error
          )}`,
          type: 'error'
        })
      }
    }
  )

  const { data, isLoading } = useQuery(
    'RTsummary',
    async () => {
      return await makeCoreGetRequest('/shop-reception-mobile/truck/hub/summary/' + truckId, null)
    },
    {
      onError: error => {
        showNotification({
          message: `Error while fetching truck. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  const handleSuccess = idx => {
    const newUploadedImages = [...uploadedImages]
    newUploadedImages[idx] = true
    setUploadedImages(newUploadedImages)

    showNotification({
      message: 'Imaginea a fost încărcată cu succes.',
      type: 'success'
    })
  }

  const handleError = error => {
    showNotification({
      message: `Error uploading image: ${error.message}`,
      type: 'error'
    })
  }

  const addSealNumberField = () => {
    if (numFields < 3) {
      setNumFields(numFields + 1)
      setSealNumbers([...sealNumbers, ''])
      setUploadedImages([...uploadedImages, false])
    }
  }

  const saveTruck = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    const sealList = sealNumbers.map(seal => ({
      id: null,
      code: seal,
      type: null,
      broken: null,
      userMessage: null
    }))

    const truck = {
      id: data?.truckId,
      shopTemperature: null,
      sealList,
      documentList: null
    }

    try {
      await makeCorePostRequest('/shop-reception-mobile/truck/hub/save/seals', truck)
      showNotification({
        message: 'Transportul a fost incarcat cu succes.',
        type: 'success'
      })

      const id = data?.truckId ?? truckId
      const path = generatePath(URLS.AUTH_REGISTER_TRUCK_STEP_4, { id: id })
      navigate(path)
    } catch (error) {
      showNotification({
        message: `Eroare la salvarea camionului. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    } finally {
      setIsSubmitting(false);
    }
  }

  const isFinalizable = sealNumbers.every(seal => seal !== '') && uploadedImages.every(img => img)

  if (isLoading) {
    return <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />
  }

  return (
    <Grid
      gridContainerStyle={{ marginTop: '20px' }}
      maxWidth="xs"
      items={[
        <Typography
          component="h2"
          variant="medium"
          style={{ textAlign: 'center', textTransform: 'uppercase' }}
          pt={2}
        >
          {hubName?.value}
        </Typography>,
        <FormInfoItems
          items={[
            { label: 'Numar inmatriculare:', name: data?.truckNumber },
            { label: 'Numar suporti scanati:', name: data?.supportCounter }
          ]}
        />,
        sealNumbers.map((seal, idx) => (
          <div style={{ marginTop: '20px' }}>
            <div>
              <Input
                key={idx}
                type="text"
                label="Număr sigiliu"
                value={sealNumbers[idx]}
                variant="outlined"
                width="100%"
                onChange={event => {
                  const newValues = [...sealNumbers]
                  newValues[idx] = (event.target as HTMLInputElement).value
                  setSealNumbers(newValues)
                }}
              />
            </div>

            <ImageUpload
              uploadUrl={`/shop-reception-mobile/truck/hub/upload/new/` + truckId}
              onSuccess={() => handleSuccess(idx)}
              onError={handleError}
            />
          </div>
        )),
        <Button
          type="button"
          variant="contained"
          label="ADAUGĂ ALT SIGILIU"
          onClick={addSealNumberField}
          style={{ width: '100%' }}
        />,
        isFinalizable && (
          <Button
            type="button"
            variant="contained"
            label="FINALIZEAZǍ"
            onClick={saveTruck}
            style={{ width: '100%' }}
            disabled={isSubmitting}
          />
        )
      ]}
      itemsPerRow={1}
    />
  )
}

export default RegisterTruckStep3
